import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, X, Zap, Package, Truck, Edit2, Trash2, Loader2, ImagePlus, CreditCard, Clock, CheckCircle, XCircle, Users, Building2, Eye, EyeOff, Search, MapPin, Send, Minus, Tag } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AlertModal from '../components/AlertModal';
import { adminAPI } from '../utils/api';

function AdminPage() {
  const [activeTab, setActiveTab] = useState('products');
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [users, setUsers] = useState([]);
  const [skus, setSkus] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  // 상품 등록/수정 폼 상태
  const [showProductForm, setShowProductForm] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [productForm, setProductForm] = useState({
    name: '',
    price: '',
    description: '',
    detail_description: '',
    image_urls: [],
    options: [],
    sku_id: '',
    product_type: 'NORMAL',
    min_quantity: '1',
    max_quantity: '',
    is_active: true,
  });

  // SKU 관리 상태
  const [showSkuForm, setShowSkuForm] = useState(false);
  const [editingSku, setEditingSku] = useState(null);
  const [skuForm, setSkuForm] = useState({
    sku_name: '',
    barcode: '',
    fassto_product_code: '',
    is_fulfillment: false,
  });
  const [uploading, setUploading] = useState(false);
  const [alertModal, setAlertModal] = useState({ isOpen: false, type: 'info', message: '' });
  const [orderStatusFilter, setOrderStatusFilter] = useState('PAID');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [userTypeFilter, setUserTypeFilter] = useState('ALL');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userForm, setUserForm] = useState({
    name: '',
    email: '',
    user_type: 'NORMAL',
    saved_address: {
      postal_code: '',
      address: '',
      detail_address: '',
      request: '',
    },
  });

  // 수동출고 상태
  const [showManualShipment, setShowManualShipment] = useState(false);
  const [fulfillmentProducts, setFulfillmentProducts] = useState([]);
  const [manualShipmentForm, setManualShipmentForm] = useState({
    recipient: '',
    phone: '',
    postal_code: '',
    address: '',
    detail_address: '',
    request: '',
    order_source: '',
    items: [], // { product_uuid, quantity, product }
  });
  const [manualShipmentLoading, setManualShipmentLoading] = useState(false);

  const showAlert = (type, message) => {
    setAlertModal({ isOpen: true, type, message });
  };

  const closeAlert = () => {
    setAlertModal({ isOpen: false, type: 'info', message: '' });
  };

  useEffect(() => {
    loadData();
  }, [activeTab]);

  const loadData = async () => {
    try {
      setLoading(true);
      if (activeTab === 'products') {
        const [productData, skuData] = await Promise.all([adminAPI.getProducts(), adminAPI.getSkus()]);
        setProducts(productData);
        setSkus(skuData);
      } else if (activeTab === 'orders') {
        const data = await adminAPI.getOrders();
        setOrders(data);
      } else if (activeTab === 'users') {
        const data = await adminAPI.getUsers();
        setUsers(data);
      } else if (activeTab === 'skus') {
        const data = await adminAPI.getSkus();
        setSkus(data);
      }
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSku = async (e) => {
    e.preventDefault();
    if (!skuForm.sku_name) {
      showAlert('error', 'SKU명은 필수입니다.');
      return;
    }
    try {
      setLoading(true);
      await adminAPI.createSku({
        sku_name: skuForm.sku_name,
        barcode: skuForm.barcode || null,
        fassto_product_code: skuForm.fassto_product_code || null,
        is_fulfillment: skuForm.is_fulfillment,
      });
      showAlert('success', 'SKU가 등록되었습니다.');
      setShowSkuForm(false);
      resetSkuForm();
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateSku = async (e) => {
    e.preventDefault();
    if (!editingSku) return;
    try {
      setLoading(true);
      await adminAPI.updateSku(editingSku.sku_id, {
        sku_name: skuForm.sku_name,
        barcode: skuForm.barcode || null,
        fassto_product_code: skuForm.fassto_product_code || null,
        is_fulfillment: skuForm.is_fulfillment,
      });
      showAlert('success', 'SKU가 수정되었습니다.');
      setShowSkuForm(false);
      setEditingSku(null);
      resetSkuForm();
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteSku = async (skuId) => {
    if (!window.confirm('이 SKU를 삭제하시겠습니까? 연결된 상품/옵션의 SKU 연결이 해제됩니다.')) return;
    try {
      await adminAPI.deleteSku(skuId);
      showAlert('success', 'SKU가 삭제되었습니다.');
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    }
  };

  const startEditSku = (sku) => {
    setEditingSku(sku);
    setSkuForm({
      sku_name: sku.sku_name,
      barcode: sku.barcode || '',
      fassto_product_code: sku.fassto_product_code || '',
      is_fulfillment: sku.is_fulfillment || false,
    });
    setShowSkuForm(true);
  };

  const resetSkuForm = () => {
    setSkuForm({ sku_name: '', barcode: '', fassto_product_code: '', is_fulfillment: false });
    setEditingSku(null);
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    try {
      setUploading(true);
      const uploadedUrls = [];

      for (const file of files) {
        const result = await adminAPI.uploadImage(file);
        uploadedUrls.push(result.url);
      }

      setProductForm({
        ...productForm,
        image_urls: [...productForm.image_urls, ...uploadedUrls]
      });
      showAlert('success', `${uploadedUrls.length}개 이미지 업로드 완료`);
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveImage = (index) => {
    const newImageUrls = productForm.image_urls.filter((_, i) => i !== index);
    setProductForm({ ...productForm, image_urls: newImageUrls });
  };

  const handleCreateProduct = async (e) => {
    e.preventDefault();
    if (!productForm.name || !productForm.price) {
      showAlert('error', '상품명과 가격은 필수입니다.');
      return;
    }

    try {
      setLoading(true);
      await adminAPI.createProduct({
        name: productForm.name,
        price: parseFloat(productForm.price),
        description: productForm.description,
        detail_description: productForm.detail_description,
        image_urls: productForm.image_urls,
        options: productForm.options.map(o => ({ name: o.name, price: parseFloat(o.price) || 0, sku_id: o.sku_id || null })),
        sku_id: productForm.sku_id || null,
        product_type: productForm.product_type,
        min_quantity: parseInt(productForm.min_quantity) || 1,
        max_quantity: productForm.max_quantity ? parseInt(productForm.max_quantity) : null,
        is_active: productForm.is_active,
      });
      showAlert('success', '상품이 등록되었습니다.');
      setShowProductForm(false);
      resetProductForm();
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;

    try {
      setLoading(true);
      await adminAPI.updateProduct(editingProduct.uuid, {
        name: productForm.name,
        price: parseFloat(productForm.price),
        description: productForm.description,
        detail_description: productForm.detail_description,
        image_urls: productForm.image_urls,
        options: productForm.options.map(o => ({ name: o.name, price: parseFloat(o.price) || 0, sku_id: o.sku_id || null })),
        sku_id: productForm.sku_id || null,
        product_type: productForm.product_type,
        min_quantity: parseInt(productForm.min_quantity) || 1,
        max_quantity: productForm.max_quantity ? parseInt(productForm.max_quantity) : null,
        is_active: productForm.is_active,
      });
      showAlert('success', '상품이 수정되었습니다.');
      setShowProductForm(false);
      setEditingProduct(null);
      resetProductForm();
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProduct = async (uuid) => {
    if (!window.confirm('정말 삭제하시겠습니까?')) return;

    try {
      await adminAPI.deleteProduct(uuid);
      showAlert('success', '상품이 삭제되었습니다.');
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    }
  };

  const startEditProduct = (product) => {
    setEditingProduct(product);
    setProductForm({
      name: product.name,
      price: product.price.toString(),
      description: product.description || '',
      detail_description: product.detail_description || '',
      image_urls: product.image_urls || (product.image_url ? [product.image_url] : []),
      options: (product.options || []).map(o => ({ name: o.name, price: o.price.toString(), sku_id: o.sku_id || '' })),
      sku_id: product.sku_id || '',
      product_type: product.product_type || 'NORMAL',
      min_quantity: (product.min_quantity || 1).toString(),
      max_quantity: product.max_quantity ? product.max_quantity.toString() : '',
      is_active: product.is_active !== false,
    });
    setShowProductForm(true);
  };

  const resetProductForm = () => {
    setProductForm({
      name: '',
      price: '',
      description: '',
      detail_description: '',
      image_urls: [],
      options: [],
      sku_id: '',
      product_type: 'NORMAL',
      min_quantity: '1',
      max_quantity: '',
      is_active: true,
    });
    setEditingProduct(null);
  };

  const startEditUser = (user) => {
    setSelectedUser(user);
    setUserForm({
      name: user.name || '',
      email: user.email || '',
      user_type: user.user_type || 'NORMAL',
      saved_address: {
        postal_code: user.saved_address?.postal_code || '',
        address: user.saved_address?.address || '',
        detail_address: user.saved_address?.detail_address || '',
        request: user.saved_address?.request || '',
      },
    });
  };

  const handleSearchAddress = () => {
    new window.daum.Postcode({
      oncomplete: function(data) {
        setUserForm(prev => ({
          ...prev,
          saved_address: {
            ...prev.saved_address,
            postal_code: data.zonecode,
            address: data.roadAddress || data.jibunAddress,
          },
        }));
      }
    }).open();
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    try {
      const updateData = {
        name: userForm.name || null,
        email: userForm.email || null,
        user_type: userForm.user_type,
        saved_address: userForm.saved_address.postal_code ? userForm.saved_address : null,
      };
      await adminAPI.updateUser(selectedUser.phone, updateData);
      showAlert('success', '사용자 정보가 수정되었습니다.');
      setSelectedUser(null);
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    }
  };

  const handleUpdateOrderStatus = async (uuid, status, trackingData = {}) => {
    try {
      await adminAPI.updateOrderStatus(uuid, status, trackingData);
      showAlert('success', '주문 상태가 변경되었습니다.');
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    }
  };

  // 수동출고 모달 열기
  const openManualShipment = async () => {
    try {
      setManualShipmentLoading(true);
      const products = await adminAPI.getFulfillmentProducts();
      setFulfillmentProducts(products);
      setManualShipmentForm({
        recipient: '',
        phone: '',
        postal_code: '',
        address: '',
        detail_address: '',
        request: '',
        items: [],
      });
      setShowManualShipment(true);
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setManualShipmentLoading(false);
    }
  };

  // 수동출고 주소 검색
  const handleManualShipmentAddressSearch = () => {
    new window.daum.Postcode({
      oncomplete: function(data) {
        setManualShipmentForm(prev => ({
          ...prev,
          postal_code: data.zonecode,
          address: data.roadAddress || data.jibunAddress,
        }));
      }
    }).open();
  };

  // 수동출고 상품 추가 (fulfillmentProducts의 flat 항목)
  const addManualShipmentItem = (fulfillmentItem) => {
    const itemId = fulfillmentItem.product_uuid + '|' + (fulfillmentItem.option_name || '');
    setManualShipmentForm(prev => {
      const existingIndex = prev.items.findIndex(item => item.item_id === itemId);
      if (existingIndex >= 0) {
        const newItems = [...prev.items];
        newItems[existingIndex] = { ...newItems[existingIndex], quantity: newItems[existingIndex].quantity + 1 };
        return { ...prev, items: newItems };
      } else {
        return {
          ...prev,
          items: [...prev.items, {
            item_id: itemId,
            product_uuid: fulfillmentItem.product_uuid,
            option_name: fulfillmentItem.option_name || null,
            quantity: 1,
            display_name: fulfillmentItem.product_name + (fulfillmentItem.option_name ? ` (${fulfillmentItem.option_name})` : ''),
            fassto_stock: fulfillmentItem.fassto_stock,
          }]
        };
      }
    });
  };

  // 수동출고 상품 수량 변경
  const updateManualShipmentItemQuantity = (itemId, quantity) => {
    if (quantity <= 0) {
      removeManualShipmentItem(itemId);
      return;
    }
    setManualShipmentForm(prev => ({
      ...prev,
      items: prev.items.map(item => item.item_id === itemId ? { ...item, quantity } : item)
    }));
  };

  // 수동출고 상품 제거
  const removeManualShipmentItem = (itemId) => {
    setManualShipmentForm(prev => ({
      ...prev,
      items: prev.items.filter(item => item.item_id !== itemId)
    }));
  };

  // 수동출고 신청
  const handleManualShipment = async () => {
    if (!manualShipmentForm.recipient || !manualShipmentForm.phone || !manualShipmentForm.address) {
      showAlert('error', '수령인, 전화번호, 주소를 입력해주세요.');
      return;
    }
    if (manualShipmentForm.items.length === 0) {
      showAlert('error', '출고할 상품을 선택해주세요.');
      return;
    }

    try {
      setManualShipmentLoading(true);
      const result = await adminAPI.createManualShipment({
        recipient: manualShipmentForm.recipient,
        phone: manualShipmentForm.phone,
        postal_code: manualShipmentForm.postal_code,
        address: manualShipmentForm.address,
        detail_address: manualShipmentForm.detail_address,
        request: manualShipmentForm.request,
        order_source: manualShipmentForm.order_source || null,
        items: manualShipmentForm.items.map(item => ({
          product_uuid: item.product_uuid,
          quantity: item.quantity,
          option_name: item.option_name || null,
        }))
      });
      showAlert('success', `수동출고 신청 완료! 출고번호: ${result.fmsSlipNo}`);
      setShowManualShipment(false);
      loadData();
    } catch (err) {
      showAlert('error', err.message);
    } finally {
      setManualShipmentLoading(false);
    }
  };

  if (loading && (products.length === 0 && orders.length === 0)) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <Header />
        <main className="flex-1 container mx-auto px-4 py-8">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-900">관리자 페이지</h1>
            <p className="text-gray-500 mt-1">상품과 주문을 관리합니다</p>
          </div>
          {/* 탭 스켈레톤 */}
          <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit animate-pulse">
            <div className="h-10 w-24 bg-gray-200 rounded-lg" />
            <div className="h-10 w-24 bg-gray-200 rounded-lg" />
          </div>
          {/* 상품 카드 스켈레톤 */}
          <div className="space-y-3">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 animate-pulse">
                <div className="flex gap-3">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex-shrink-0" />
                  <div className="flex-1">
                    <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                    <div className="h-5 bg-gray-200 rounded w-1/3 mb-2" />
                    <div className="flex gap-2">
                      <div className="h-5 bg-gray-200 rounded w-16" />
                      <div className="h-5 bg-gray-200 rounded w-12" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <Header />

      <main className="flex-1 container mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">관리자 페이지</h1>
          <p className="text-gray-500 mt-1">상품과 주문을 관리합니다</p>
        </div>

        {/* 탭 */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          <button
            onClick={() => setActiveTab('products')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'products'
                ? 'bg-white text-teal-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Package className="w-4 h-4" />
            상품 관리
          </button>
          <button
            onClick={() => setActiveTab('orders')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'orders'
                ? 'bg-white text-teal-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Truck className="w-4 h-4" />
            주문 관리
          </button>
          <button
            onClick={() => setActiveTab('skus')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'skus'
                ? 'bg-white text-teal-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Tag className="w-4 h-4" />
            SKU 관리
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              activeTab === 'users'
                ? 'bg-white text-teal-600 shadow-sm'
                : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            <Users className="w-4 h-4" />
            회원 관리
          </button>
        </div>

        {/* 상품 관리 탭 */}
        {activeTab === 'products' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">상품 목록</h2>
              <button
                onClick={() => {
                  resetProductForm();
                  setShowProductForm(true);
                }}
                className="bg-teal-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                상품 등록
              </button>
            </div>

            {/* 상품 등록/수정 폼 */}
            {showProductForm && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <div className="flex justify-between items-center mb-6">
                  <h3 className="text-lg font-bold text-gray-900">
                    {editingProduct ? '상품 수정' : '상품 등록'}
                  </h3>
                  <button
                    onClick={() => {
                      setShowProductForm(false);
                      setEditingProduct(null);
                      resetProductForm();
                    }}
                    className="text-gray-500 hover:text-gray-700"
                  >
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={editingProduct ? handleUpdateProduct : handleCreateProduct}>
                  <div className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">상품명 *</label>
                        <input
                          type="text"
                          value={productForm.name}
                          onChange={(e) => setProductForm({ ...productForm, name: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          placeholder="상품 이름"
                          required
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">가격 *</label>
                        <input
                          type="number"
                          value={productForm.price}
                          onChange={(e) => setProductForm({ ...productForm, price: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                          placeholder="10000"
                          required
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상품 설명 (짧은 한 줄 설명)</label>
                      <textarea
                        value={productForm.description}
                        onChange={(e) => setProductForm({ ...productForm, description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        rows="2"
                        placeholder="상품 리스트에 표시될 짧은 설명을 입력하세요."
                      ></textarea>
                    </div>

                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <label className="block text-sm font-medium text-gray-700">상세 설명 (Markdown 지원)</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={async (e) => {
                            const file = e.target.files?.[0];
                            if (!file) return;
                            try {
                              setUploading(true);
                              const result = await adminAPI.uploadImage(file);
                              const markdownImage = `<div align="center">\n<img src="${result.url}" />\n</div>`;
                              setProductForm(prev => ({
                                ...prev,
                                detail_description: prev.detail_description
                                  ? prev.detail_description + '\n\n' + markdownImage
                                  : markdownImage
                              }));
                              showAlert('success', '이미지가 삽입되었습니다.');
                            } catch (err) {
                              showAlert('error', err.message);
                            } finally {
                              setUploading(false);
                              e.target.value = '';
                            }
                          }}
                          className="hidden"
                          id="markdown-image-upload"
                        />
                        <label
                          htmlFor="markdown-image-upload"
                          className={`inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded cursor-pointer transition-colors ${
                            uploading
                              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                              : 'bg-teal-50 text-teal-700 hover:bg-teal-100'
                          }`}
                        >
                          {uploading ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <ImagePlus className="w-3 h-3" />
                          )}
                          이미지 삽입
                        </label>
                      </div>
                      <textarea
                        value={productForm.detail_description}
                        onChange={(e) => setProductForm({ ...productForm, detail_description: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-mono text-sm"
                        rows="8"
                        placeholder="상품 상세 페이지에 표시될 내용을 입력하세요. Markdown 문법 사용 가능합니다."
                      ></textarea>
                      <p className="text-xs text-gray-500 mt-1">
                        Markdown 문법과 HTML 태그를 사용할 수 있습니다.
                      </p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상품 이미지</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                        <input
                          type="file"
                          accept="image/*"
                          multiple
                          onChange={handleImageUpload}
                          disabled={uploading}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="flex flex-col items-center justify-center cursor-pointer"
                        >
                          {uploading ? (
                            <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                          ) : (
                            <ImagePlus className="w-8 h-8 text-gray-400" />
                          )}
                          <span className="text-sm text-gray-500 mt-2">
                            {uploading ? '업로드 중...' : '클릭하여 이미지 업로드 (여러 개 선택 가능)'}
                          </span>
                        </label>
                      </div>
                      {productForm.image_urls.length > 0 && (
                        <div className="mt-3 grid grid-cols-4 sm:grid-cols-6 gap-3">
                          {productForm.image_urls.map((url, index) => (
                            <div key={index} className="relative group aspect-square">
                              <img
                                src={url}
                                alt={`Preview ${index + 1}`}
                                className="w-full h-full object-cover rounded-lg"
                              />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                className="absolute top-1 right-1 bg-red-500 text-white rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity"
                              >
                                <X className="w-3 h-3" />
                              </button>
                              <div className="absolute bottom-1 left-1 bg-black/60 text-white text-xs px-1.5 py-0.5 rounded">
                                {index + 1}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 상품 타입 */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-2">상품 공개 범위</label>
                      <div className="flex flex-wrap gap-2">
                        {[
                          { value: 'NORMAL', label: '전체 공개', icon: Eye, desc: '모든 사용자에게 표시' },
                          { value: 'B2B', label: 'B2B 전용', icon: Building2, desc: 'B2B 회원에게만 표시' },
                          { value: 'HIDDEN', label: '숨김', icon: EyeOff, desc: '목록에 표시 안됨' },
                        ].map(({ value, label, icon: Icon, desc }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setProductForm({ ...productForm, product_type: value })}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              productForm.product_type === value
                                ? 'bg-teal-100 text-teal-700 border-2 border-teal-300'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            <Icon className="w-4 h-4" />
                            <div className="text-left">
                              <div>{label}</div>
                              <div className="text-xs opacity-70">{desc}</div>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* 상품 옵션 */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <div className="flex items-center justify-between mb-3">
                        <div>
                          <label className="block text-sm font-medium text-gray-700">상품 옵션</label>
                          <p className="text-xs text-gray-500 mt-0.5">옵션이 있으면 고객이 선택 후 구매합니다. 각 옵션마다 가격을 지정하세요.</p>
                        </div>
                        <button
                          type="button"
                          onClick={() => setProductForm({
                            ...productForm,
                            options: [...productForm.options, { name: '', price: '', sku_id: '' }]
                          })}
                          className="flex items-center gap-1.5 px-3 py-1.5 bg-teal-50 text-teal-700 hover:bg-teal-100 text-sm font-medium rounded-lg transition-colors"
                        >
                          <Plus className="w-3.5 h-3.5" />
                          옵션 추가
                        </button>
                      </div>
                      {productForm.options.length === 0 ? (
                        <p className="text-xs text-gray-400 text-center py-3">옵션 없음 (옵션 없이 가격으로 판매)</p>
                      ) : (
                        <div className="space-y-3">
                          {productForm.options.map((opt, idx) => (
                            <div key={idx} className="border border-gray-200 rounded-lg p-3 bg-white">
                              <div className="flex gap-2 items-center mb-2">
                                <input
                                  type="text"
                                  value={opt.name}
                                  onChange={(e) => {
                                    const newOptions = [...productForm.options];
                                    newOptions[idx] = { ...newOptions[idx], name: e.target.value };
                                    setProductForm({ ...productForm, options: newOptions });
                                  }}
                                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                                  placeholder="옵션명 (예: 블랙, L사이즈, 딸기맛)"
                                />
                                <input
                                  type="number"
                                  value={opt.price}
                                  onChange={(e) => {
                                    const newOptions = [...productForm.options];
                                    newOptions[idx] = { ...newOptions[idx], price: e.target.value };
                                    setProductForm({ ...productForm, options: newOptions });
                                  }}
                                  className="w-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm"
                                  placeholder="가격 (원)"
                                  min="0"
                                />
                                <button
                                  type="button"
                                  onClick={() => {
                                    const newOptions = productForm.options.filter((_, i) => i !== idx);
                                    setProductForm({ ...productForm, options: newOptions });
                                  }}
                                  className="p-2 text-gray-400 hover:text-red-500 transition-colors flex-shrink-0"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                              {/* 옵션별 SKU 연결 */}
                              <div className="flex items-center gap-2 mt-1">
                                <Tag className="w-3 h-3 text-teal-500 flex-shrink-0" />
                                <select
                                  value={opt.sku_id || ''}
                                  onChange={(e) => {
                                    const newOptions = [...productForm.options];
                                    newOptions[idx] = { ...newOptions[idx], sku_id: e.target.value };
                                    setProductForm({ ...productForm, options: newOptions });
                                  }}
                                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-1 focus:ring-teal-500"
                                >
                                  <option value="">SKU 연결 안함</option>
                                  {skus.map(sku => (
                                    <option key={sku.sku_id} value={sku.sku_id}>
                                      {sku.sku_name}{sku.is_fulfillment ? ' ⚡' : ''}{sku.fassto_product_code ? ` (${sku.fassto_product_code})` : ''}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>

                    {/* 구매 수량 제한 */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="block text-sm font-medium text-gray-700 mb-3">구매 수량 제한</label>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">최소 수량</label>
                          <input
                            type="number"
                            min="1"
                            value={productForm.min_quantity}
                            onChange={(e) => setProductForm({ ...productForm, min_quantity: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            placeholder="1"
                          />
                        </div>
                        <div>
                          <label className="block text-xs text-gray-500 mb-1">최대 수량 (비워두면 무제한)</label>
                          <input
                            type="number"
                            min="1"
                            value={productForm.max_quantity}
                            onChange={(e) => setProductForm({ ...productForm, max_quantity: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                            placeholder="무제한"
                          />
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-2">
                        고객이 한 번에 구매할 수 있는 수량을 제한합니다.
                      </p>
                    </div>

                    {/* SKU 연결 (옵션 없는 상품) */}
                    {productForm.options.length === 0 && (
                      <div className="bg-gray-50 rounded-lg p-4">
                        <label className="block text-sm font-medium text-gray-700 mb-2 flex items-center gap-1">
                          <Tag className="w-4 h-4 text-teal-500" />
                          SKU 연결
                        </label>
                        <select
                          value={productForm.sku_id}
                          onChange={(e) => setProductForm({ ...productForm, sku_id: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        >
                          <option value="">SKU 연결 안함</option>
                          {skus.map(sku => (
                            <option key={sku.sku_id} value={sku.sku_id}>
                              {sku.sku_name}{sku.is_fulfillment ? ' ⚡' : ''}{sku.fassto_product_code ? ` (${sku.fassto_product_code})` : ''}
                            </option>
                          ))}
                        </select>
                        <p className="text-xs text-gray-500 mt-1">
                          SKU를 연결하면 파스토 재고와 빠른 배송 여부가 자동으로 적용됩니다.
                        </p>
                      </div>
                    )}

                    {/* 상품 활성화 상태 */}
                    <div className="bg-gray-50 rounded-lg p-4">
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={productForm.is_active}
                          onChange={(e) => setProductForm({ ...productForm, is_active: e.target.checked })}
                          className="w-4 h-4 text-teal-500 rounded focus:ring-teal-500"
                        />
                        <span className="font-medium text-gray-900 flex items-center gap-1">
                          {productForm.is_active ? (
                            <Eye className="w-4 h-4 text-teal-500" />
                          ) : (
                            <EyeOff className="w-4 h-4 text-gray-400" />
                          )}
                          상품 활성화
                        </span>
                      </label>
                      <p className="text-xs text-gray-500 mt-2 ml-6">
                        비활성화하면 상품이 쇼핑몰에 표시되지 않습니다.
                      </p>
                    </div>

                    <div className="flex gap-3 pt-4">
                      <button
                        type="submit"
                        disabled={loading}
                        className="flex-1 bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
                      >
                        {editingProduct ? '수정 완료' : '등록하기'}
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setShowProductForm(false);
                          setEditingProduct(null);
                          resetProductForm();
                        }}
                        className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                      >
                        취소
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            )}

            {/* 상품 목록 - 모바일: 카드, 데스크탑: 테이블 */}
            {/* 모바일 카드 레이아웃 */}
            <div className="md:hidden space-y-3">
              {products.map((product) => {
                const images = product.image_urls || (product.image_url ? [product.image_url] : []);
                return (
                  <div key={product.uuid} className="bg-white rounded-xl border border-gray-200 p-4">
                    <div className="flex gap-3">
                      {/* 이미지 */}
                      <div className="flex-shrink-0">
                        {images.length > 0 ? (
                          <div className="relative">
                            <img
                              src={images[0]}
                              alt={product.name}
                              className="w-16 h-16 object-cover rounded-lg"
                            />
                            {images.length > 1 && (
                              <span className="absolute -bottom-1 -right-1 text-xs text-white bg-black/60 px-1.5 py-0.5 rounded">
                                +{images.length - 1}
                              </span>
                            )}
                          </div>
                        ) : (
                          <div className="w-16 h-16 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs">
                            없음
                          </div>
                        )}
                      </div>
                      {/* 상품 정보 */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{product.name}</h3>
                        <p className="text-teal-600 font-bold mt-0.5">{product.price.toLocaleString()}원</p>
                        <div className="flex items-center gap-2 mt-2 flex-wrap">
                          {product.options && product.options.length > 0 && (
                            <span className="inline-flex items-center gap-1 text-xs bg-orange-50 text-orange-700 px-2 py-0.5 rounded">
                              옵션 {product.options.length}개
                            </span>
                          )}
                          {(product.sku_id || product.options?.some(o => o.sku_id)) && (
                            <span className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded">
                              <Zap className="w-3 h-3" />
                              빠른배송
                            </span>
                          )}
                          <span className={`text-xs px-2 py-0.5 rounded ${
                            product.product_type === 'B2B'
                              ? 'bg-purple-50 text-purple-700'
                              : product.product_type === 'HIDDEN'
                              ? 'bg-gray-100 text-gray-500'
                              : 'bg-blue-50 text-blue-700'
                          }`}>
                            {product.product_type === 'B2B' ? 'B2B' : product.product_type === 'HIDDEN' ? '숨김' : '전체'}
                          </span>
                          {!product.is_active && (
                            <span className="text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-500">
                              비활성
                            </span>
                          )}
                        </div>
                      </div>
                      {/* 액션 버튼 */}
                      <div className="flex flex-col gap-2">
                        <button
                          onClick={() => startEditProduct(product)}
                          className="p-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                          title="수정"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDeleteProduct(product.uuid)}
                          className="p-2 text-gray-600 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="삭제"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              {products.length === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">등록된 상품이 없습니다.</p>
                </div>
              )}
            </div>

            {/* 데스크탑 테이블 레이아웃 */}
            <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">이미지</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">상품명</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">가격</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">공개범위</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">풀필먼트</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {products.map((product) => {
                      const images = product.image_urls || (product.image_url ? [product.image_url] : []);
                      return (
                        <tr key={product.uuid} className="hover:bg-gray-50">
                          <td className="px-6 py-4">
                            {images.length > 0 ? (
                              <div className="flex items-center gap-1">
                                <img
                                  src={images[0]}
                                  alt={product.name}
                                  className="w-12 h-12 object-cover rounded-lg"
                                />
                                {images.length > 1 && (
                                  <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
                                    +{images.length - 1}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400 text-xs">
                                없음
                              </div>
                            )}
                          </td>
                          <td className="px-6 py-4 font-medium text-gray-900">{product.name}</td>
                          <td className="px-6 py-4 text-teal-600 font-medium">{product.price.toLocaleString()}원</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              product.product_type === 'B2B'
                                ? 'bg-purple-50 text-purple-700 border border-purple-200'
                                : product.product_type === 'HIDDEN'
                                ? 'bg-gray-50 text-gray-600 border border-gray-200'
                                : 'bg-blue-50 text-blue-700 border border-blue-200'
                            }`}>
                              {product.product_type === 'B2B' ? 'B2B' : product.product_type === 'HIDDEN' ? '숨김' : '전체'}
                            </span>
                          </td>
                          <td className="px-6 py-4">
                            {(product.sku_id || product.options?.some(o => o.sku_id)) ? (
                              <div className="flex items-center gap-1 text-teal-600">
                                <Zap className="w-4 h-4" />
                                <span className="text-sm font-medium">사용</span>
                              </div>
                            ) : (
                              <span className="text-gray-400 text-sm">미사용</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="flex items-center gap-2">
                              {!product.is_active && (
                                <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-50 text-gray-600 border border-gray-200">
                                  비활성
                                </span>
                              )}
                              <button
                                onClick={() => startEditProduct(product)}
                                className="text-gray-600 hover:text-teal-600 transition-colors"
                                title="수정"
                              >
                                <Edit2 className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDeleteProduct(product.uuid)}
                                className="text-gray-600 hover:text-red-600 transition-colors"
                                title="삭제"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {products.length === 0 && (
                <div className="text-center py-12">
                  <Package className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">등록된 상품이 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 주문 관리 탭 */}
        {activeTab === 'orders' && (
          <div>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold text-gray-900">주문 목록</h2>
              <button
                onClick={openManualShipment}
                disabled={manualShipmentLoading}
                className="bg-teal-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors flex items-center gap-2 disabled:opacity-50"
              >
                {manualShipmentLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Send className="w-4 h-4" />
                )}
                수동출고
              </button>
            </div>

            {/* 주문 상태 필터 탭 */}
            <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
              {[
                { status: 'PAID', label: '결제완료', icon: CreditCard, color: 'teal' },
                { status: 'PROCESSING', label: '배송준비', icon: Clock, color: 'blue' },
                { status: 'SHIPPED', label: '배송중', icon: Truck, color: 'purple' },
                { status: 'DELIVERED', label: '배송완료', icon: CheckCircle, color: 'gray' },
              ].map(({ status, label, icon: Icon, color }) => {
                const count = orders.filter(o => o.status === status).length;
                const isActive = orderStatusFilter === status;
                return (
                  <button
                    key={status}
                    onClick={() => setOrderStatusFilter(status)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                      isActive
                        ? `bg-${color}-50 text-${color}-700 border-2 border-${color}-200`
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4" />
                    {label}
                    {count > 0 && (
                      <span className={`text-xs px-1.5 py-0.5 rounded-full ${
                        isActive ? `bg-${color}-200 text-${color}-800` : 'bg-gray-100 text-gray-600'
                      }`}>
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            {/* 주문 목록 - 카드 형태 */}
            <div className="space-y-3">
              {orders
                .filter(order => order.status === orderStatusFilter)
                .map((order) => (
                  <div
                    key={order.uuid}
                    onClick={() => setSelectedOrder(order)}
                    className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-teal-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start gap-3">
                      {/* 상품 이미지 썸네일 */}
                      <div className="flex-shrink-0">
                        {order.items && order.items[0]?.product?.image_urls?.[0] ? (
                          <img
                            src={order.items[0].product.image_urls[0]}
                            alt=""
                            className="w-14 h-14 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-14 h-14 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="w-6 h-6 text-gray-400" />
                          </div>
                        )}
                      </div>
                      {/* 주문 정보 */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <p className="font-medium text-gray-900 truncate">
                            {order.items && order.items.length > 0
                              ? `${order.items[0].product.name}${order.items.length > 1 ? ` 외 ${order.items.length - 1}건` : ''}`
                              : '상품 정보 없음'}
                          </p>
                          {order.has_fast_shipping && (
                            <Zap className="w-4 h-4 text-teal-500 flex-shrink-0" />
                          )}
                        </div>
                        <p className="text-sm text-gray-500">
                          {order.address?.recipient} · {new Date(order.created_at).toLocaleDateString('ko-KR')}
                        </p>
                        <p className="text-teal-600 font-bold mt-1">
                          {order.amount?.toLocaleString() || 0}원
                        </p>
                      </div>
                    </div>
                  </div>
                ))}

              {orders.filter(order => order.status === orderStatusFilter).length === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <Truck className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">해당 상태의 주문이 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* SKU 관리 탭 */}
        {activeTab === 'skus' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">SKU 목록</h2>
              <button
                onClick={() => { resetSkuForm(); setShowSkuForm(true); }}
                className="bg-teal-500 text-white px-4 py-2 rounded-lg font-medium hover:bg-teal-600 transition-colors flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                SKU 등록
              </button>
            </div>

            {/* SKU 등록/수정 폼 */}
            {showSkuForm && (
              <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
                <div className="flex justify-between items-center mb-4">
                  <h3 className="text-lg font-bold text-gray-900">{editingSku ? 'SKU 수정' : 'SKU 등록'}</h3>
                  <button onClick={() => { setShowSkuForm(false); resetSkuForm(); }} className="text-gray-500 hover:text-gray-700">
                    <X className="w-5 h-5" />
                  </button>
                </div>
                <form onSubmit={editingSku ? handleUpdateSku : handleCreateSku}>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">SKU명 *</label>
                      <input
                        type="text"
                        value={skuForm.sku_name}
                        onChange={(e) => setSkuForm({ ...skuForm, sku_name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="예: 치즈 클리커"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">바코드</label>
                      <input
                        type="text"
                        value={skuForm.barcode}
                        onChange={(e) => setSkuForm({ ...skuForm, barcode: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="바코드 번호"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">파스토 상품코드 (cstGodCd)</label>
                      <input
                        type="text"
                        value={skuForm.fassto_product_code}
                        onChange={(e) => setSkuForm({ ...skuForm, fassto_product_code: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500"
                        placeholder="예: PROD001"
                      />
                    </div>
                    <div className="flex items-center">
                      <label className="flex items-center gap-2 cursor-pointer mt-5">
                        <input
                          type="checkbox"
                          checked={skuForm.is_fulfillment}
                          onChange={(e) => setSkuForm({ ...skuForm, is_fulfillment: e.target.checked })}
                          className="w-4 h-4 text-teal-500 rounded focus:ring-teal-500"
                        />
                        <span className="font-medium text-gray-900 flex items-center gap-1">
                          <Zap className="w-4 h-4 text-teal-500" />
                          파스토 풀필먼트 사용 (빠른 배송)
                        </span>
                      </label>
                    </div>
                  </div>
                  <div className="flex gap-3">
                    <button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-teal-500 text-white py-2.5 rounded-lg font-medium hover:bg-teal-600 disabled:bg-gray-400 transition-colors"
                    >
                      {editingSku ? '수정 완료' : '등록하기'}
                    </button>
                    <button
                      type="button"
                      onClick={() => { setShowSkuForm(false); resetSkuForm(); }}
                      className="px-6 py-2.5 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                    >
                      취소
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* SKU 목록 테이블 */}
            <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">SKU명</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">바코드</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">파스토 코드</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">풀필먼트</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">재고</th>
                      <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">관리</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {skus.map((sku) => (
                      <tr key={sku.sku_id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 font-medium text-gray-900">{sku.sku_name}</td>
                        <td className="px-6 py-4 text-gray-500 font-mono text-sm">{sku.barcode || '-'}</td>
                        <td className="px-6 py-4 text-gray-500 font-mono text-sm">{sku.fassto_product_code || '-'}</td>
                        <td className="px-6 py-4">
                          {sku.is_fulfillment ? (
                            <span className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded-full border border-teal-200">
                              <Zap className="w-3 h-3" />
                              사용
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">미사용</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {sku.is_fulfillment ? (
                            <span className={`text-sm font-medium ${sku.fassto_stock_qty > 0 ? 'text-teal-600' : 'text-gray-400'}`}>
                              {sku.fassto_stock_qty ?? '-'}개
                            </span>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => startEditSku(sku)}
                              className="text-gray-600 hover:text-teal-600 transition-colors"
                              title="수정"
                            >
                              <Edit2 className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => handleDeleteSku(sku.sku_id)}
                              className="text-gray-600 hover:text-red-600 transition-colors"
                              title="삭제"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {skus.length === 0 && (
                <div className="text-center py-12">
                  <Tag className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">등록된 SKU가 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 회원 관리 탭 */}
        {activeTab === 'users' && (
          <div>
            <div className="mb-6 flex justify-between items-center">
              <h2 className="text-lg font-bold text-gray-900">회원 목록</h2>
              <div className="flex gap-2">
                {[
                  { value: 'ALL', label: '전체' },
                  { value: 'NORMAL', label: '일반' },
                  { value: 'B2B', label: 'B2B' },
                ].map(({ value, label }) => (
                  <button
                    key={value}
                    onClick={() => setUserTypeFilter(value)}
                    className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                      userTypeFilter === value
                        ? 'bg-teal-100 text-teal-700'
                        : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* 유저 목록 - 모바일 */}
            <div className="md:hidden space-y-3">
              {users
                .filter(u => userTypeFilter === 'ALL' || u.user_type === userTypeFilter)
                .map((user) => (
                <div
                  key={user.phone}
                  onClick={() => startEditUser(user)}
                  className="bg-white rounded-xl border border-gray-200 p-4 cursor-pointer hover:border-teal-300 hover:shadow-md transition-all"
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{user.name || '이름 없음'}</h3>
                        {user.user_type === 'B2B' && (
                          <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                            <Building2 className="w-3 h-3" />
                            B2B
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-gray-500 mt-1">{user.phone}</p>
                      {user.email && <p className="text-xs text-gray-400">{user.email}</p>}
                      {user.saved_address?.address && (
                        <p className="text-xs text-gray-400 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" />
                          {user.saved_address.address}
                        </p>
                      )}
                      <p className="text-xs text-gray-400 mt-1">
                        가입일: {new Date(user.created_at).toLocaleDateString('ko-KR')}
                      </p>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); startEditUser(user); }}
                      className="p-2 text-gray-600 hover:text-teal-600 hover:bg-teal-50 rounded-lg transition-colors"
                    >
                      <Edit2 className="w-5 h-5" />
                    </button>
                  </div>
                </div>
              ))}
              {users.filter(u => userTypeFilter === 'ALL' || u.user_type === userTypeFilter).length === 0 && (
                <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">등록된 회원이 없습니다.</p>
                </div>
              )}
            </div>

            {/* 유저 목록 - 데스크탑 */}
            <div className="hidden md:block bg-white rounded-xl border border-gray-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">이름</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">전화번호</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">이메일</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">회원 타입</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">저장된 주소</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">가입일</th>
                    <th className="px-6 py-3 text-left text-sm font-medium text-gray-500">관리</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {users
                    .filter(u => userTypeFilter === 'ALL' || u.user_type === userTypeFilter)
                    .map((user) => (
                    <tr key={user.phone} className="hover:bg-gray-50">
                      <td className="px-6 py-4 font-medium text-gray-900">
                        <div className="flex items-center gap-2">
                          {user.name || '이름 없음'}
                          {user.user_type === 'B2B' && (
                            <span className="inline-flex items-center gap-1 text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded-full">
                              <Building2 className="w-3 h-3" />
                              B2B
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-gray-600 font-mono text-sm">{user.phone}</td>
                      <td className="px-6 py-4 text-gray-600 text-sm">{user.email || '-'}</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          user.user_type === 'B2B'
                            ? 'bg-purple-50 text-purple-700 border border-purple-200'
                            : 'bg-gray-50 text-gray-600 border border-gray-200'
                        }`}>
                          {user.user_type === 'B2B' ? 'B2B' : '일반'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm max-w-xs truncate">
                        {user.saved_address?.address ? (
                          <span className="flex items-center gap-1">
                            <MapPin className="w-3 h-3 flex-shrink-0" />
                            {user.saved_address.address}
                          </span>
                        ) : '-'}
                      </td>
                      <td className="px-6 py-4 text-gray-500 text-sm">
                        {new Date(user.created_at).toLocaleDateString('ko-KR')}
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => startEditUser(user)}
                          className="text-gray-600 hover:text-teal-600 transition-colors"
                          title="수정"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {users.filter(u => userTypeFilter === 'ALL' || u.user_type === userTypeFilter).length === 0 && (
                <div className="text-center py-12">
                  <Users className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">등록된 회원이 없습니다.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 주문 상세 모달 */}
        {selectedOrder && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
            onClick={() => setSelectedOrder(null)}
          >
            <div
              className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
                <div className="min-w-0 flex-1 mr-4">
                  <h2 className="text-lg font-bold text-gray-900">주문 상세</h2>
                  <p className="text-sm text-gray-500 font-mono break-all">주문번호: {selectedOrder.uuid}</p>
                </div>
                <button
                  onClick={() => setSelectedOrder(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors flex-shrink-0"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                {/* 주문 상태 변경 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">주문 상태</p>
                  <div className="flex flex-wrap gap-2">
                    {[
                      { status: 'PAID', label: '결제완료', icon: CreditCard },
                      { status: 'PROCESSING', label: '처리중', icon: Clock },
                      { status: 'SHIPPED', label: '배송중', icon: Truck },
                      { status: 'DELIVERED', label: '완료', icon: CheckCircle },
                      { status: 'CANCELLED', label: '취소', icon: XCircle },
                    ].map(({ status, label, icon: Icon }) => (
                      <button
                        key={status}
                        onClick={() => {
                          handleUpdateOrderStatus(selectedOrder.uuid, status);
                          setSelectedOrder({ ...selectedOrder, status });
                        }}
                        className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          selectedOrder.status === status
                            ? status === 'CANCELLED'
                              ? 'bg-red-100 text-red-700 border-2 border-red-300'
                              : 'bg-teal-100 text-teal-700 border-2 border-teal-300'
                            : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                        }`}
                      >
                        <Icon className="w-4 h-4" />
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 고객 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">고객 정보</p>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <p className="text-gray-500">수령인</p>
                      <p className="font-medium text-gray-900">{selectedOrder.address?.recipient}</p>
                    </div>
                    <div>
                      <p className="text-gray-500">연락처</p>
                      <p className="font-medium text-gray-900">{selectedOrder.address?.phone || selectedOrder.user_phone}</p>
                    </div>
                    <div className="col-span-2">
                      <p className="text-gray-500">주소</p>
                      <p className="font-medium text-gray-900">
                        ({selectedOrder.address?.postal_code}) {selectedOrder.address?.address}
                      </p>
                      {selectedOrder.address?.detail_address && (
                        <p className="text-gray-700">{selectedOrder.address.detail_address}</p>
                      )}
                    </div>
                    {selectedOrder.address?.request && (
                      <div className="col-span-2">
                        <p className="text-gray-500">배송 요청사항</p>
                        <p className="font-medium text-gray-900">{selectedOrder.address.request}</p>
                      </div>
                    )}
                  </div>
                </div>

                {/* 배송 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">배송 정보</p>
                  <div className="grid grid-cols-4 gap-2">
                    <div className="col-span-1">
                      <label className="block text-xs text-gray-500 mb-1">택배사</label>
                      <input
                        type="text"
                        value={selectedOrder.courier_name || ''}
                        onChange={(e) => setSelectedOrder({ ...selectedOrder, courier_name: e.target.value })}
                        placeholder="CJ대한통운"
                        className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      />
                    </div>
                    <div className="col-span-3">
                      <label className="block text-xs text-gray-500 mb-1">운송장번호</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={selectedOrder.tracking_no || ''}
                          onChange={(e) => setSelectedOrder({ ...selectedOrder, tracking_no: e.target.value })}
                          placeholder="운송장번호 입력"
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 font-mono"
                        />
                        <button
                          onClick={() => {
                            if (selectedOrder.tracking_no && selectedOrder.courier_name) {
                              handleUpdateOrderStatus(selectedOrder.uuid, selectedOrder.status, {
                                tracking_no: selectedOrder.tracking_no,
                                courier_name: selectedOrder.courier_name,
                              });
                            }
                          }}
                          disabled={!selectedOrder.tracking_no || !selectedOrder.courier_name}
                          className="px-4 py-2 bg-teal-500 text-white text-sm font-medium rounded-lg hover:bg-teal-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                        >
                          저장
                        </button>
                      </div>
                    </div>
                  </div>
                  {selectedOrder.has_fast_shipping && selectedOrder.fmsSlipNo && (
                    <div className="mt-3 pt-3 border-t border-gray-200">
                      <div className="flex items-center gap-1 text-teal-600 text-sm">
                        <Zap className="w-4 h-4" />
                        <span>파스토 출고번호: {selectedOrder.fmsSlipNo}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* 주문 상품 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">주문 상품</p>
                  <div className="space-y-3">
                    {selectedOrder.items?.map((item, idx) => (
                      <div key={idx} className="flex items-center gap-3 bg-white p-3 rounded-lg">
                        {item.product.image_urls?.[0] ? (
                          <img
                            src={item.product.image_urls[0]}
                            alt={item.product.name}
                            className="w-12 h-12 object-cover rounded-lg"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                            <Package className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-gray-900 truncate">{item.product.name}</p>
                          <p className="text-sm text-gray-500">
                            {item.product.price?.toLocaleString()}원 × {item.quantity}개
                          </p>
                        </div>
                        <div className="text-right">
                          {item.has_fast_shipping && (
                            <span className="inline-flex items-center gap-1 text-xs bg-teal-50 text-teal-700 px-2 py-0.5 rounded mb-1">
                              <Zap className="w-3 h-3" />
                              빠른배송
                            </span>
                          )}
                          <p className="font-bold text-teal-600">
                            {((item.product.price || 0) * item.quantity).toLocaleString()}원
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* 결제 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">결제 정보</p>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-500">상품 금액</span>
                      <span className="text-gray-900">{selectedOrder.subtotal?.toLocaleString() || 0}원</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">배송비</span>
                      <span className="text-gray-900">{selectedOrder.shipping_fee?.toLocaleString() || 0}원</span>
                    </div>
                    {selectedOrder.discount > 0 && (
                      <div className="flex justify-between text-red-600">
                        <span>할인</span>
                        <span>-{selectedOrder.discount?.toLocaleString()}원</span>
                      </div>
                    )}
                    <div className="flex justify-between pt-3 border-t border-gray-200 text-base font-bold">
                      <span className="text-gray-900">총 결제금액</span>
                      <span className="text-teal-600">{selectedOrder.amount?.toLocaleString() || 0}원</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* 유저 수정 모달 */}
        {selectedUser && (
          <div
            className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
            onClick={() => setSelectedUser(null)}
          >
            <div
              className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              {/* 모달 헤더 */}
              <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
                <div>
                  <h2 className="text-lg font-bold text-gray-900">회원 정보 수정</h2>
                  <p className="text-sm text-gray-500 font-mono">{selectedUser.phone}</p>
                </div>
                <button
                  onClick={() => setSelectedUser(null)}
                  className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              <div className="p-4 space-y-4">
                {/* 기본 정보 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <p className="text-sm font-medium text-gray-700 mb-3">기본 정보</p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">이름</label>
                      <input
                        type="text"
                        value={userForm.name}
                        onChange={(e) => setUserForm({ ...userForm, name: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="홍길동"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">이메일</label>
                      <input
                        type="email"
                        value={userForm.email}
                        onChange={(e) => setUserForm({ ...userForm, email: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="example@email.com"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">회원 타입</label>
                      <div className="flex gap-2">
                        {[
                          { value: 'NORMAL', label: '일반' },
                          { value: 'B2B', label: 'B2B', icon: Building2 },
                        ].map(({ value, label, icon: Icon }) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setUserForm({ ...userForm, user_type: value })}
                            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                              userForm.user_type === value
                                ? value === 'B2B'
                                  ? 'bg-purple-100 text-purple-700 border-2 border-purple-300'
                                  : 'bg-teal-100 text-teal-700 border-2 border-teal-300'
                                : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                            }`}
                          >
                            {Icon && <Icon className="w-4 h-4" />}
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>

                {/* 저장된 주소 */}
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <MapPin className="w-4 h-4 text-gray-600" />
                    <p className="text-sm font-medium text-gray-700">저장된 배송 주소</p>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">우편번호</label>
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={userForm.saved_address.postal_code}
                          readOnly
                          className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                          placeholder="우편번호"
                        />
                        <button
                          type="button"
                          onClick={handleSearchAddress}
                          className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center gap-2 whitespace-nowrap"
                        >
                          <Search className="w-4 h-4" />
                          검색
                        </button>
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">주소</label>
                      <input
                        type="text"
                        value={userForm.saved_address.address}
                        readOnly
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                        placeholder="주소 검색 버튼을 눌러주세요"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">상세주소</label>
                      <input
                        type="text"
                        value={userForm.saved_address.detail_address}
                        onChange={(e) => setUserForm({
                          ...userForm,
                          saved_address: { ...userForm.saved_address, detail_address: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="101동 101호"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">배송 요청사항</label>
                      <textarea
                        value={userForm.saved_address.request}
                        onChange={(e) => setUserForm({
                          ...userForm,
                          saved_address: { ...userForm.saved_address, request: e.target.value }
                        })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        rows="2"
                        placeholder="문 앞에 놔주세요."
                      ></textarea>
                    </div>
                  </div>
                </div>

                {/* 버튼 */}
                <div className="flex gap-3 pt-2">
                  <button
                    onClick={handleUpdateUser}
                    className="flex-1 bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors"
                  >
                    저장
                  </button>
                  <button
                    onClick={() => setSelectedUser(null)}
                    className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  >
                    취소
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </main>

      <Footer />


      {/* 수동출고 모달 */}
      {showManualShipment && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm"
          onClick={() => setShowManualShipment(false)}
        >
          <div
            className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* 모달 헤더 */}
            <div className="sticky top-0 bg-white border-b border-gray-200 p-4 flex justify-between items-center">
              <div>
                <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  <Send className="w-5 h-5 text-teal-500" />
                  수동출고 신청
                </h2>
                <p className="text-sm text-gray-500">결제 없이 직접 출고를 요청합니다</p>
              </div>
              <button
                onClick={() => setShowManualShipment(false)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-4 space-y-4">
              {/* 판매 채널 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">판매 채널 (주문처)</p>
                <select
                  value={manualShipmentForm.order_source}
                  onChange={(e) => setManualShipmentForm({ ...manualShipmentForm, order_source: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500 bg-white"
                >
                  <option value="">선택 안 함</option>
                  <option value="레이어블샵">레이어블샵</option>
                  <option value="TEMU">TEMU</option>
                  <option value="스마트스토어">스마트스토어</option>
                  <option value="에이블리">에이블리</option>
                  <option value="기타">기타</option>
                </select>
              </div>

              {/* 배송 정보 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">배송 정보</p>
                <div className="space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">수령인 *</label>
                      <input
                        type="text"
                        value={manualShipmentForm.recipient}
                        onChange={(e) => setManualShipmentForm({ ...manualShipmentForm, recipient: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="홍길동"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">전화번호 *</label>
                      <input
                        type="tel"
                        value={manualShipmentForm.phone}
                        onChange={(e) => setManualShipmentForm({ ...manualShipmentForm, phone: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                        placeholder="010-0000-0000"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">주소 *</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={manualShipmentForm.postal_code}
                        readOnly
                        className="w-24 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                        placeholder="우편번호"
                      />
                      <input
                        type="text"
                        value={manualShipmentForm.address}
                        readOnly
                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg bg-gray-100"
                        placeholder="주소 검색을 눌러주세요"
                      />
                      <button
                        type="button"
                        onClick={handleManualShipmentAddressSearch}
                        className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-900 transition-colors flex items-center gap-2 whitespace-nowrap"
                      >
                        <Search className="w-4 h-4" />
                        검색
                      </button>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">상세주소</label>
                    <input
                      type="text"
                      value={manualShipmentForm.detail_address}
                      onChange={(e) => setManualShipmentForm({ ...manualShipmentForm, detail_address: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="101동 101호"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">배송 요청사항</label>
                    <input
                      type="text"
                      value={manualShipmentForm.request}
                      onChange={(e) => setManualShipmentForm({ ...manualShipmentForm, request: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-teal-500"
                      placeholder="문 앞에 놔주세요"
                    />
                  </div>
                </div>
              </div>

              {/* 상품 선택 */}
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-sm font-medium text-gray-700 mb-3">빠른출고 가능 상품</p>
                <div className="space-y-2 max-h-48 overflow-y-auto">
                  {fulfillmentProducts.length === 0 ? (
                    <p className="text-sm text-gray-500 text-center py-4">빠른출고 가능한 상품이 없습니다.</p>
                  ) : (
                    fulfillmentProducts.map((fi) => {
                      const itemId = fi.product_uuid + '|' + (fi.option_name || '');
                      const selectedItem = manualShipmentForm.items.find(item => item.item_id === itemId);
                      return (
                        <div
                          key={itemId}
                          className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${
                            selectedItem ? 'bg-teal-50 border border-teal-200' : 'bg-white border border-gray-200 hover:border-teal-200'
                          }`}
                        >
                          {fi.image_urls?.[0] ? (
                            <img
                              src={fi.image_urls[0]}
                              alt={fi.product_name}
                              className="w-10 h-10 object-cover rounded-lg flex-shrink-0"
                            />
                          ) : (
                            <div className="w-10 h-10 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
                              <Package className="w-4 h-4 text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-gray-900 text-sm truncate">{fi.product_name}</p>
                            {fi.option_name && (
                              <span className="text-xs bg-orange-50 text-orange-700 px-1.5 py-0.5 rounded">{fi.option_name}</span>
                            )}
                            <p className="text-xs text-gray-500 mt-0.5">
                              재고: {fi.fassto_stock}개 · {fi.price?.toLocaleString()}원
                            </p>
                          </div>
                          {selectedItem ? (
                            <div className="flex items-center gap-2">
                              <button
                                type="button"
                                onClick={() => updateManualShipmentItemQuantity(itemId, selectedItem.quantity - 1)}
                                className="w-7 h-7 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-lg transition-colors"
                              >
                                <Minus className="w-3 h-3" />
                              </button>
                              <span className="w-8 text-center font-medium text-sm">{selectedItem.quantity}</span>
                              <button
                                type="button"
                                onClick={() => updateManualShipmentItemQuantity(itemId, selectedItem.quantity + 1)}
                                disabled={selectedItem.quantity >= fi.fassto_stock}
                                className="w-7 h-7 flex items-center justify-center bg-teal-500 hover:bg-teal-600 text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <Plus className="w-3 h-3" />
                              </button>
                            </div>
                          ) : (
                            <button
                              type="button"
                              onClick={() => addManualShipmentItem(fi)}
                              disabled={fi.fassto_stock <= 0}
                              className="px-3 py-1.5 text-sm bg-teal-500 text-white rounded-lg hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              추가
                            </button>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>

              {/* 선택된 상품 목록 */}
              {manualShipmentForm.items.length > 0 && (
                <div className="bg-teal-50 rounded-lg p-4 border border-teal-200">
                  <p className="text-sm font-medium text-teal-700 mb-3">출고 상품 ({manualShipmentForm.items.length}종)</p>
                  <div className="space-y-2">
                    {manualShipmentForm.items.map((item) => (
                      <div key={item.item_id} className="flex items-center justify-between text-sm">
                        <span className="text-gray-900">{item.display_name}</span>
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-teal-700">{item.quantity}개</span>
                          <button
                            type="button"
                            onClick={() => removeManualShipmentItem(item.item_id)}
                            className="text-red-500 hover:text-red-700"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 버튼 */}
              <div className="flex gap-3 pt-2">
                <button
                  onClick={handleManualShipment}
                  disabled={manualShipmentLoading || manualShipmentForm.items.length === 0}
                  className="flex-1 bg-teal-500 text-white py-3 rounded-lg font-medium hover:bg-teal-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {manualShipmentLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      처리 중...
                    </>
                  ) : (
                    <>
                      <Send className="w-4 h-4" />
                      출고 신청
                    </>
                  )}
                </button>
                <button
                  onClick={() => setShowManualShipment(false)}
                  className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <AlertModal
        isOpen={alertModal.isOpen}
        onClose={closeAlert}
        type={alertModal.type}
        message={alertModal.message}
      />
    </div>
  );
}

export default AdminPage;
